"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AccountClient = void 0;
var tslib_1 = require("tslib");
var commerce_core_1 = require("@broadleaf/commerce-core");
/**
 * Client used for interacting with the Account APIs for customer which may represent things like a B2B business account.
 */
var AccountClient = /** @class */ (function (_super) {
    tslib_1.__extends(AccountClient, _super);
    function AccountClient(options) {
        return _super.call(this, tslib_1.__assign(tslib_1.__assign({}, options), { basePath: options.basePath || '/api/customer/accounts' })) || this;
    }
    /**
     * Retrieves a list of the accounts the current customer is a member of. This can be configured to
     * include the hierarchy below those accounts that customer is an immediate member of. The depth
     * of the hierarchy to return is controlled by `subAccountDepth` where `0` means no subaccounts and
     * `-1` means unlimited depth, e.g., subaccounts and their subaccounts ad infinitum until all leaf
     * subaccounts are found.
     *
     * @param subAccountDepth the depth of the account hierarchy to return. `-1` indicates the entire
     *     hierarchy of all accounts the customer is a member of. Default is `0` meaning no subaccounts.
     * @param cq RSQL filters to pass in the request if any
     * @param options the options to the HTTP call
     *
     * @return a list of customer accounts
     */
    AccountClient.prototype.getCustomersAccounts = function (subAccountDepth, cq, options) {
        if (subAccountDepth === void 0) { subAccountDepth = 0; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'get',
                            url: "/customer-accounts/my-accounts",
                            params: {
                                cq: cq,
                                subAccountDepth: subAccountDepth
                            }
                        };
                        this.attachPageableParamsIfPresent(config, options);
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Returns a page of the specified account's members.
     *
     * @param accountId ID of the account whose members to retrieve
     * @param options Additional request options like pagination params
     * @returns A page of the specified account's members.
     */
    AccountClient.prototype.listAccountMembers = function (accountId, params, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'get',
                            url: "/customer-accounts/" + accountId,
                            params: params
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Returns a member of the specified account with the matching ID
     *
     * @param accountId ID of the account whose member we will retrieve
     * @param memberId ID of the member belonging to the account to be retrieved
     * @param options Additional request options to the HTTP call
     * @returns A member matching the specified ID and account
     */
    AccountClient.prototype.readAccountMember = function (accountId, memberId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'get',
                            url: "/customer-accounts/" + accountId + "/account-member/" + memberId
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Updates an account member's data
     *
     * @param accountId ID of the account whose member we will update
     * @param memberId ID of the member belonging to the account to be updated
     * @param accountMember Object representing the data to use for the update
     * @param options Additional request options to the HTTP call
     * @returns an updated AccountMember object
     */
    AccountClient.prototype.updateAccountMember = function (accountId, memberId, accountMember, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'put',
                            url: "/customer-accounts/" + accountId + "/account-member/" + memberId,
                            data: accountMember
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Removes an account member from the account
     *
     * @param accountId ID of the account whose member we will remove
     * @param memberId ID of the member belonging to the account to be removed
     * @param options Additional request options to the HTTP call
     */
    AccountClient.prototype.deleteAccountMember = function (accountId, memberId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'delete',
                            url: "/customer-accounts/" + accountId + "/account-member/" + memberId
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Retrieves a page of all invites for an account
     *
     * @param accountId ID of the account whose invites we will list
     * @param options Additional request options like pagination params
     * @returns A page with the list of invites for an account
     */
    AccountClient.prototype.listAllInvites = function (accountId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'get',
                            url: "/" + accountId + "/account-invites",
                            params: {}
                        };
                        this.attachPageableParamsIfPresent(config, options);
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Sends an invite for the specified account
     *
     * @param accountId ID of the account whose member we will invite
     * @param accountInvite Object representing the details for the invite
     * @param options Additional request options to the HTTP call
     * @returns An AccountInvite object with the details for the invite
     */
    AccountClient.prototype.inviteAccountMember = function (accountId, accountInvite, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'post',
                            url: "/" + accountId + "/account-invites",
                            data: accountInvite
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Revokes the invite for the specified account
     *
     * @param accountId ID of the account whose invite we will revoke
     * @param inviteId ID of the invite
     * @param memberId ID of the member invited
     * @param options Additional request options to the HTTP call
     */
    AccountClient.prototype.revokeInvite = function (accountId, inviteId, accountMemberId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'delete',
                            url: "/" + accountId + "/account-invites/" + inviteId,
                            params: {
                                accountMemberId: accountMemberId
                            }
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Resends an invite for the specified account
     *
     * @param accountId ID of the account whose invite we will resend
     * @param inviteId ID of the invite
     * @param options Additional request options to the HTTP call
     * @returns An AccountInvite object with the details for the invite
     */
    AccountClient.prototype.resendInvite = function (accountId, inviteId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'post',
                            url: "/" + accountId + "/account-invites/" + inviteId + "/resend-invite"
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Accepts an invite to the specified account
     *
     * @param accountId ID of the account whose invite we will accept
     * @param inviteId ID of the invite
     * @param options Additional request options to the HTTP call
     * @returns An AccountMember object
     */
    AccountClient.prototype.acceptInvite = function (accountId, inviteId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'post',
                            url: "/" + accountId + "/account-invites/" + inviteId + "/accept-invite"
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Retrieves a page of all invites for the logged in user
     *
     * @param options Additional request options like pagination params
     * @returns A page with the list of invites for an account
     */
    AccountClient.prototype.readMyAccountInvites = function (options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'get',
                            url: "/my-account-invites",
                            params: {}
                        };
                        this.attachPageableParamsIfPresent(config, options);
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Retrieves a page of account addresses.
     *
     * @param accountId the account id to retrieve addresses for
     * @param options the options to the HTTP call
     * @return a page of account addresses
     */
    AccountClient.prototype.listAccountAddresses = function (accountId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'get',
                            url: "/" + accountId + "/addresses",
                            params: {}
                        };
                        this.attachPageableParamsIfPresent(config, options);
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Retrieves a single account address by account Id and account address Id.
     *
     * @param accountId the account id to fetch
     * @param accountAddressId the account id to fetch
     * @param options the options to the HTTP call
     * @return a single Account Address
     */
    AccountClient.prototype.getAccountAddress = function (accountId, accountAddressId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'get',
                            url: "/" + accountId + "/addresses/" + accountAddressId
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Adds a new account address to an existing account.
     *
     * @param accountId the account id to add an address to
     * @param accountAddress the address to add
     * @param options the options to the HTTP call
     * @return the new account address
     */
    AccountClient.prototype.addAccountAddress = function (accountId, accountAddress, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'post',
                            url: "/" + accountId + "/addresses",
                            data: accountAddress
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Updates an existing account address.
     *
     * @param accountId the account id which owns the address
     * @param accountAddressId the account address id to update
     * @param accountAddress the updated address data
     * @param options the options to the HTTP call
     * @return the updated account address
     */
    AccountClient.prototype.updateAccountAddress = function (accountId, accountAddressId, accountAddress, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'put',
                            url: "/" + accountId + "/addresses/" + accountAddressId,
                            data: accountAddress
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Deletes an account address.
     *
     * @param accountId the account id which owns the address
     * @param accountAddressId the account address id to delete
     * @param options the options to the HTTP call
     */
    AccountClient.prototype.deleteAccountAddress = function (accountId, accountAddressId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'delete',
                            url: "/" + accountId + "/addresses/" + accountAddressId
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Retrieves the order history for an account
     *
     * @param includeChildren indicates if the account orders of children accounts will be included
     * @param cq the RSQL filter/s to be applied
     * @param options the options to the HTTP call
     * @return the account's order history
     */
    AccountClient.prototype.listAccountOrderHistory = function (includeChildren, cq, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'get',
                            url: "/order-history/account",
                            baseURL: this.baseHost + "/api/order-operations",
                            params: { includeChildren: includeChildren, cq: cq }
                        };
                        this.attachPageableParamsIfPresent(config, options);
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Retrieves an order by id or order number for the account
     *
     * @param orderRef the order's id or order number
     * @param includeChildren indicates if the account orders of children accounts will be included
     * @param options the options to the HTTP call
     * @return the requested order
     */
    AccountClient.prototype.getAccountOrder = function (orderRef, includeChildren, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = {
                            method: 'get',
                            url: "/order-history/account/" + orderRef,
                            baseURL: this.baseHost + "/api/order-operations",
                            params: { includeChildren: includeChildren }
                        };
                        return [4 /*yield*/, this.call(config, options)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response === null || response === void 0 ? void 0 : response.data];
                }
            });
        });
    };
    /**
     * Attaches pageable params (size, sort, etc.) to the request `config` if present on `options`.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    AccountClient.prototype.attachPageableParamsIfPresent = function (config, options) {
        if (options) {
            var page = options.page, size = options.size, sort = options.sort;
            if (page || page === 0) {
                config.params.page = page;
            }
            if (size || size === 0) {
                config.params.size = size;
            }
            if (sort) {
                config.params.sort = sort;
            }
        }
    };
    return AccountClient;
}(commerce_core_1.AbstractClient));
exports.AccountClient = AccountClient;
//# sourceMappingURL=account-client.js.map